/**
 * Debt Fight Club - Language System
 *
 * Voice: Dom's directness × Goggins' brutality × Spartan discipline.
 * Think: your best mate who also happens to be a Spartan training master
 * who's sick of watching you throw money away.
 *
 * Tone: Stoic warrior discipline. Endurance. Grit. Mental toughness.
 * Think 300 training montage, not the battlefield.
 *
 * Rules:
 *  1. Short. Punchy. No filler.
 *  2. Mean but loving. The cruelty IS the kindness.
 *  3. Debt is the enemy. Money paid off is a victory. Freedom is the only mission.
 *  4. No corporate language. No "metrics". No "analytics". No "insights".
 *  5. Mock weakness. Celebrate wins. No participation trophies.
 *  6. Commands, not suggestions. "Cancel them" not "consider cancelling".
 *  7. If it sounds like a banking app wrote it, bin it.
 *  8. No war/battle language. Fighter discipline, not military ops.
 */

// ─── APP SHELL ──────────────────────────────────────────
export const app = {
  name: 'Debt Fight Club',
  tagline: 'Cry Harder.',
  version: 'v4.0',
  footer: 'The Pit',
} as const;

// ─── NAVIGATION ─────────────────────────────────────────
export const nav = {
  dashboard: 'Dashboard',
  warfare: 'Hit List',
  ai: 'The Pit',
} as const;

// ─── LANDING PAGE ───────────────────────────────────────
export const landing = {
  title: ['DEBT', 'FIGHT CLUB'],
  subtitle: 'No Excuses. No Mercy. No Debt.',
  cta: 'Enter the Arena',
} as const;

// ─── DASHBOARD ──────────────────────────────────────────
export const dashboard = {
  upload: {
    heading: 'Show Me the Damage',
    description: 'Drop a CSV and take responsibility. Your data, your risk, your fight.',
    idle: 'Drop CSV. Own It.',
    uploading: 'Ripping it apart...',
    complete: 'Exposed. Deal with it.',
    disclaimer: 'Your file. Your data. Your problem. We don\'t store it, sell it, or care about it after you close this tab.',
  },
  metrics: {
    streak:     { label: 'No-Spend Streak', suffix: ' Days' },
    suffering:  { label: 'Pain Tolerance',  suffix: '' },
    interest:   { label: 'Money Burned',    prefix: '£' },
    readiness:  { label: 'Fight Ready',     suffix: '%' },
  },
  sidebar: {
    heading: 'The Hard Truth',
    quote: '"Nobody cares about your excuses. Pay the debt."',
  },
} as const;

// ─── ALERTS ─────────────────────────────────────────────
export const alerts = {
  critical: {
    label: 'PATHETIC',
    takeaway: '£145 on takeaway this week. You\'re literally eating your future. Cry harder.',
  },
  warning: {
    label: 'SLIPPING',
    subscriptions: '3 subscriptions you forgot about. £47/month gone while you sleep like a baby.',
  },
} as const;

// ─── HIT LIST (debt strategy + tracking) ────────────────
export const warBoard = {
  sections: {
    strategy:  'Game Plan',
    warChest:  'Fight Fund',
    targets:   'Targets',
  },
  strategies: {
    avalanche: {
      name: 'Avalanche',
      description: 'Highest interest first. Maximum damage per pound. The smart way.',
    },
    snowball: {
      name: 'Snowball',
      description: 'Smallest balance first. Quick wins. For the emotionally fragile.',
    },
  },
  topMetrics: {
    totalDebt:    'What You Owe',
    crushed:      'Destroyed',
    freedom:      'Freedom Date',
    monthlyPower: 'Monthly Power',
  },
  progress: 'Road to Zero',
  sortLabels: {
    interest: 'Sorted by threat level',
    balance:  'Sorted by size',
  },
  slider: {
    minimum: 'BARE MINIMUM',
    extra:   'ACTUALLY TRYING',
  },
  charts: {
    spending:  'Where It Goes',
    breakdown: 'The Autopsy',
  },
  categories: {
    housing:       'Housing',
    food:          'Food',
    transport:     'Transport',
    debtPayments:  'Debt Payments',
    entertainment: 'Distractions',
    other:         'Other Excuses',
  },
} as const;

// ─── DEBT CARDS ─────────────────────────────────────────
export const debtCard = {
  priority: {
    high:   'THREAT',
    medium: 'TARGET',
    low:    'MINOR',
  },
  stats: {
    balance: 'OWES',
    apr:     'APR',
    paidOff: 'CRUSHED',
  },
  payment: (amount: number) => `£${amount}/mo minimum. Do better.`,
} as const;

// ─── THE PIT (AI analysis) ──────────────────────────────
export const warRoom = {
  heading: 'The Pit',
  subtitle: (count: number) => `${count} things you don't want to hear`,
  emptyState: 'Upload a statement. Or keep pretending everything\'s fine.',
  insightTypes: {
    critical: 'CRITICAL',
    warning:  'WARNING',
    success:  'EARNED',
    tip:      'LESSON',
  },
  defaultInsights: {
    assessment: {
      title: 'Reality Check',
      message: 'Your spending has more holes than your excuses. We found every one.',
      detail: 'Recurring subscriptions and impulse buys. The ones you\'re too lazy to cancel.',
      action: 'Face the Full Truth',
    },
    highSpending: {
      title: 'Burn Rate',
      message: (amount: string) => `£${amount} a month. Gone. You worked for that and then threw it in the bin.`,
      detail: 'Your top 5 spending categories. Find 20% to cut this month. Not next month. This month.',
      action: 'Show Me the Damage',
    },
    negativeCashFlow: {
      title: 'On the Ropes',
      message: (amount: string) => `£${amount} in the red every single month. How long did you think this would last?`,
      detail: '30-day spending freeze on everything that isn\'t keeping you alive. Cancel every subscription you forgot existed.',
      action: 'Lockdown Mode',
    },
    psychological: {
      title: 'Discipline Training',
      message: 'Name your debts. You don\'t eliminate "outstanding balances". You destroy enemies with names.',
      detail: 'Every debt gets a name. Every payment is ground gained. Your brain needs targets, not spreadsheets.',
      action: 'Name Your Enemies',
    },
    momentum: {
      title: 'Don\'t Get Comfortable',
      message: 'You made a payment. Congratulations. You did the bare minimum. Now do it again.',
      detail: 'Month 6 is where most people quit. Month 6 is where the compound effect starts working for you, not against you. Don\'t be most people.',
      action: 'Keep Going',
    },
  },
} as const;

// ─── COMBINED EXPORT ────────────────────────────────────
export const language = {
  app,
  nav,
  landing,
  dashboard,
  alerts,
  warBoard,
  debtCard,
  warRoom,
} as const;

export default language;
